import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from 'components/Modal';
import {
  OrderSettings,
  CustomerInfo,
  PaymentInfo,
  ProductList,
  ActionMenuPopup,
  PropertyInfo,
} from './components';
import styles from './OrderDetailsModal.module.scss';
import {
  Order,
  SelectOption,
  Address,
  DataTableSchemaItem,
  OrderType,
} from 'types';
import ActivityNotesModal from './components/ActivityNotesModal';
import {
  formatOrderNumber,
  formatAddress,
  getCustomerName,
  getCustomerPhone,
  calculatePaymentTotals,
  getPriority,
  calculatePayableTotals,
} from './helpers';
import MessageModal from 'pages/RequestDetails/components/MessageModal';
import {useOrderDetails} from './hooks';
import DeleteModal from 'components/DeleteModal';
import PayModal from './components/PayModal';
import toast from 'react-hot-toast';
import useSendCustomerNotification from 'hooks/useSendCustomerNotification';
import useCurrentUser from 'hooks/useCurrentUser';
import {useHistory} from 'react-router-dom';
import {get} from 'lodash';
import CardModal from './components/CardModal';
import useUserForm from 'pages/UserForm/useUserForm';
import AddressListModal from './components/AddressListModal';
import useUpdateDeliveryAddress from 'pages/RequestDetails/useUpdateDeliveryAddress';
import AddAddressModal from './components/AddAddressModal';
import classNames from 'classnames';
import NoteForWorkerModal from './components/NoteForWorkerModal';
import ManualChargeModal from 'pages/RequestDetails/components/ManualChargeModal';
import CheckIcon from 'pages/Requests/CheckIcon';
import Select from 'components/Select';
import EditModal from 'pages/RequestDetails/components/EditModal';
import ServiceList from './components/ServiceList';
import PayableInfo from './components/PayableInfo';
import StatusBadge from 'components/StatusBadge';
import FeatureGuard from 'components/FeatureGuard';
import useCompanyConfig from 'hooks/useCompanyConfig';
import OrganizerBySequence from 'components/OrganizerBySequence';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
  refetchOrders: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  order,
  refetchOrders,
}) => {
  const uniqueOrders: Order[] = useMemo(() => {
    return order?.orders?.length > 0
      ? Array.from(
          order?.orders
            .reduce((map, order) => {
              map.set(order?.sequential_order_number, order);
              return map;
            }, new Map<number, Order>())
            .values()
        )
      : [];
  }, [order]);
  const isMergedOrder = uniqueOrders?.length > 1;

  const [activeTab, setActiveTab] = useState(uniqueOrders[0] || ({} as Order));
  const [isActivityNotesModalOpen, setIsActivityNotesModalOpen] =
    useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption>();
  const [sendChannels, setSendChannels] = useState({
    sms: true,
    email: false,
    push: false,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddressListModal, setShowAddressListModal] = useState(false);
  const [showNewAddressModal, setShowNewAddressModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showNoteForWorkerModal, setShowNoteForWorkerModal] = useState(false);
  const {isDeliveryManager, layoutConfig} = useCompanyConfig();

  const orderNumber = formatOrderNumber(order);
  const customerName = getCustomerName(order);
  const customerPhone = getCustomerPhone(order);
  const customerAddress = formatAddress(order.delivery_address as Address);

  const priority = getPriority(order);

  const {
    data,
    landlord,
    messageTemplates,
    sendMessage,
    deleteOrder,
    message,
    setMessage,
    showMessageModal,
    setShowMessageModal,
    onChangeStatus,
    onChangeTime,
    onChangePriority,
    getPaymentUrl,
    paymentUrl,
    refetch,
    chargeCard,
    copayItems,
    setCopayItems,
    allSelected,
    setAllSelected,
    isCharging,
    orderItems,
    form,
    onChargeManually,
    showEditModal,
    setShowEditModal,
    editType,
    setEditType,
    manualChargeType,
    setManualChargeType,
  } = useOrderDetails(
    isMergedOrder ? activeTab?.sequential_order_number?.toString() : orderNumber
  );
  const paymentTotals = calculatePaymentTotals(data?.prescriptions || []);
  const payableTotals = calculatePayableTotals(data?.prescriptions || []);
  const {sendNotification} = useSendCustomerNotification();
  const {addressIsUpdating, updateDeliveryAddress} = useUpdateDeliveryAddress();
  const currentUser = useCurrentUser();
  const {push} = useHistory();
  const [cardId, setCardIs] = useState('');
  const {cards, createNewAddress} = useUserForm(data?.customer.id);
  const [deliveryAddressId, setDeliveryAddressId] = useState<
    number | undefined
  >(
    data?.delivery_address?.id ? Number(data?.delivery_address?.id) : undefined
  );
  const [showChargeModal, setShowChargeModal] = useState(false);
  const {isPickupDropoff, isPropertyManager} = useCompanyConfig();
  useEffect(() => {
    if (isMergedOrder) {
      setActiveTab(uniqueOrders[0] || ({} as Order));
    }
  }, [isMergedOrder, uniqueOrders]);

  useEffect(() => {
    if (cards.length > 0) {
      const card = cards.find((item) => item.is_primary);
      setCardIs(card ? card.custom_uuid : cards[0].custom_uuid);
    }
  }, [cards]);

  useEffect(() => {
    if (data?.delivery_address) {
      setDeliveryAddressId(
        data?.delivery_address?.id
          ? Number(data?.delivery_address?.id)
          : undefined
      );
    }
  }, [data?.delivery_address]);

  const handleNotifyCustomer = useCallback(
    async (types: any, items: any[]) => {
      if (!get(currentUser, 'company.payment_integration')) {
        toast.error('Please, setup payment integration to notify customer');
        push('/settings/integrations', {
          show_modal: true,
        });
        return;
      }

      if (!data?.id) {
        return;
      }

      if (data.prescriptions.length > 0) {
        await sendNotification({
          requestId: data?.id,
          types: types,
          request_drugs: items,
        });
        refetch();
        toast.success('Notification sent successfully');
      }
    },
    [
      data?.id,
      currentUser,
      sendNotification,
      data?.prescriptions,
      refetch,
      push,
    ]
  );

  const onCharge = async () => {
    if (cards.length < 1) {
      toast.error('Please add billing card to user first');
      sessionStorage.setItem('user_tab', 'cards');
      push(`/people/customers/${data?.customer.id}/edit`);
      return;
    }
    setShowCardModal(true);
  };

  const onEditProduct = (type: 'service' | 'product') => {
    setEditType(type);
    setShowEditModal(true);
  };

  const addressChangeHandler = async (id: number | string) => {
    updateDeliveryAddress({
      requestId: data?.id,
      addressId: id,
    })
      .then((res) => {
        setShowAddressListModal(false);
        refetchOrders();
        refetch();
      })
      .catch((er) => {
        toast.error("You can't change address!");
        console.log('Error on updating:', er);
      });
  };
  const selectedCard = useMemo(
    () => cards.find((item) => item.custom_uuid === cardId),
    [cards, cardId]
  );

  const orderedServices = useMemo(() => {
    if (isDeliveryManager) return [];
    return data?.prescriptions?.filter(
      (item) => item.offering_type === 'service'
    );
  }, [data?.prescriptions, isDeliveryManager]);

  const orderedProducts = useMemo(() => {
    return data?.prescriptions?.filter(
      (item) => item.offering_type === 'product'
    );
  }, [data?.prescriptions]);

  const renderHeader = useCallback(() => {
    return (
      <>
        {uniqueOrders?.map((item) => (
          <div
            key={item.sequential_order_number}
            className={classNames(styles.orderNumber, {
              [styles.activeTab]:
                activeTab?.sequential_order_number ===
                item.sequential_order_number,
            })}
            onClick={() => setActiveTab(item)}
          >
            <h1>Order #{item.sequential_order_number}</h1>
          </div>
        ))}
      </>
    );
  }, [uniqueOrders, activeTab]);

  const renderOrderType = (order_type?: OrderType) => {
    if (order_type === 'pickup') {
      return (
        <StatusBadge color="default" className={styles.orderTypeBadge}>
          Purchase Order
        </StatusBadge>
      );
    }
    if (order_type === 'dropoff') {
      return (
        <StatusBadge color="green" className={styles.orderTypeBadge}>
          Customer Order
        </StatusBadge>
      );
    }
    return null;
  };
  const ORDER_ITEMS_SCHEMA: DataTableSchemaItem[] = useMemo(
    () => [
      {
        dataKey: 'checkbox',
        header:
          copayItems.length > 0 ? (
            <div
              onClick={(val) => {
                val.stopPropagation();
              }}
              className={styles.selectAll}
            >
              <div
                onClick={(val) => {
                  setAllSelected(false);
                  setCopayItems([]);
                }}
                className={styles.selectAllIcon}
              >
                <CheckIcon emptySelect={false} allSelected={allSelected} />
              </div>
              <Select
                components={{
                  Input: () => null,
                  ValueContainer: () => null,
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: 'none',
                    backgroundColor: 'transparent',
                  }),
                  container: (base) => ({
                    ...base,
                    width: 70,
                  }),
                  option: (base) => ({
                    ...base,
                    paddingTop: 3,
                    paddingBottom: 3,
                    minHeight: 'auto !important',
                  }),
                  menuList: (base) => ({
                    ...base,
                    gap: 0,
                    padding: 0,
                  }),
                }}
                options={[
                  {label: 'All', value: 'all'},
                  {label: 'None', value: 'none'},
                ]}
                onChange={(val) => {
                  if (val?.value === 'all') {
                    setCopayItems(
                      data?.prescriptions?.map((item) => ({
                        id: item.id,
                        amount_due:
                          manualChargeType === 'payable'
                            ? item.payable_amount_due
                            : item.amount_due || 0,
                      })) || []
                    );
                    setAllSelected(true);
                  } else if (val?.value === 'none') {
                    setCopayItems([]);
                    setAllSelected(false);
                  }
                }}
              />
            </div>
          ) : (
            <div style={{height: 40}}></div>
          ),
        sortable: false,
        colWidth: '60px',
      },
      {
        dataKey: 'name',
        header: 'Item Name',
        sortable: false,
        colWidth: '200px',
      },
      {
        dataKey: 'quantity',
        header: 'Quantity',
        colWidth: '80px',
      },
      {
        dataKey: 'type',
        header: 'Offering',
        colWidth: '50px',
      },
      {
        dataKey:
          manualChargeType === 'payable' ? 'payable_amount_due' : 'amount_due',
        header: 'Amount Due',
        colWidth: '60px',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      copayItems,
      allSelected,
      isDeliveryManager,
      data?.prescriptions,
      manualChargeType,
    ]
  );
  const isNotifyToPayDisabled = useMemo(() => {
    return (
      data?.prescriptions?.filter((item) => Number(item.amount_due) > 0)
        .length === 0 ||
      (data?.status === 'completed'
        ? data?.prescriptions?.filter((item) => item.status !== 'completed')
            .length === 0
        : false)
    );
  }, [data]);

  const isChargeDisabled = useMemo(() => {
    return (
      data?.prescriptions?.filter((item) => Number(item.amount_due) > 0)
        .length === 0 || data?.status === 'completed'
    );
  }, [data]);

  const isPayableChargeDisabled = useMemo(() => {
    return (
      data?.prescriptions?.filter((item) => Number(item.payable_amount_due) > 0)
        .length === 0 || data?.status === 'completed'
    );
  }, [data]);
  // console.log(data);
  return (
    <Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className={styles.modal}
        overlayClassName={styles.overlay}
      >
        <div className={styles.modalContent}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.orderInfo}>
              {isMergedOrder && renderHeader()}
              {!isMergedOrder && (
                <div className={styles.orderNumber}>
                  <h1>
                    Order #{order?.source_id || order?.sequential_order_number}
                    <span className={styles.orderTypeBadge}>
                      {renderOrderType(order?.order_type)}
                    </span>
                  </h1>
                </div>
              )}
            </div>

            <div className={styles.actionButtons}>
              <ActionMenuPopup
                onMessageCustomer={() => setShowMessageModal(true)}
                onActivityNotesOpen={() => setIsActivityNotesModalOpen(true)}
                onDeleteOrder={() => setShowDeleteModal(true)}
                onNoteForWorkerOpen={() => setShowNoteForWorkerModal(true)}
              />
            </div>
          </div>
          <div
            className={classNames({
              [styles.defaultContent]: !isMergedOrder,
              [styles.segmentedContent]: isMergedOrder,
              [styles.noTopLeftRadius]:
                isMergedOrder &&
                activeTab.sequential_order_number ===
                  uniqueOrders[0].sequential_order_number,
            })}
          >
            <div
              className={classNames(isPickupDropoff && styles.resellerContent, {
                [styles.pickup]: data?.order_type === 'pickup',
                [styles.dropoff]: data?.order_type === 'dropoff',
              })}
            >
              {/* Customer Section */}
              <CustomerInfo
                customer={{
                  name:
                    data?.customer?.client_role === 'vendors'
                      ? data?.customer?.customer_company || ''
                      : customerName,
                  phone: customerPhone,
                  address: customerAddress,
                  id: data?.customer.id,
                }}
                onEdit={() => setShowAddressListModal(true)}
              />
              <OrganizerBySequence
                sequences={layoutConfig.resellerContentSequence}
              >
                {/* Order Settings Section */}
                <OrderSettings
                  key="1"
                  orderNumber={
                    activeTab && activeTab?.sequential_order_number
                      ? activeTab?.sequential_order_number?.toString()
                      : order?.source_id?.toString() ||
                        order?.sequential_order_number?.toString()
                  }
                  orderType={order?.order_type || data?.order_type}
                  description={data?.description as string | null}
                  status={order?.orders?.[0]?.status!}
                  priority={priority}
                  preferenceDate={data?.preference_date as string | null}
                  preferenceTime={data?.preference_time as string | null}
                  onStatusChange={async (newStatus) => {
                    await onChangeStatus(
                      newStatus,
                      data?.prescriptions?.[0]?.id as number
                    );
                    refetchOrders();
                    onClose();
                  }}
                  onPriorityChange={(newPriority) => {
                    onChangePriority?.(
                      newPriority as 'medium' | 'high' | 'low'
                    );
                    refetchOrders();
                  }}
                  onChangePreferenceTime={(time) => {
                    onChangeTime(time);
                    refetchOrders();
                  }}
                />
                {/* Property Section */}
                {data?.company_property && (
                  <PropertyInfo
                    key="2"
                    property={data?.company_property}
                    landlord={landlord!}
                    description={data?.description as string}
                    propertyImages={data?.images}
                  />
                )}
                <FeatureGuard key="3" feature="hasMoneyOutOnOrderDetails">
                  {((isPickupDropoff && data?.order_type === 'pickup') ||
                    isPropertyManager) && (
                    <PayableInfo
                      payment={payableTotals}
                      onChargeManually={() => {
                        setManualChargeType('payable');
                        setShowChargeModal(true);
                      }}
                      isManualChargeDisabled={isPayableChargeDisabled}
                    />
                  )}
                </FeatureGuard>
                {/* Payment Section */}
                <PaymentInfo
                  key="4"
                  payment={paymentTotals}
                  orderType={data?.order_type}
                  onNotifyToPay={() => {
                    setShowPaymentModal(true);
                    getPaymentUrl();
                  }}
                  onCharge={onCharge}
                  onChargeManually={() => {
                    setManualChargeType('receivable');
                    setShowChargeModal(true);
                  }}
                  isNotifyToPayDisabled={isNotifyToPayDisabled}
                  isManualChargeDisabled={isNotifyToPayDisabled}
                  isChargeDisabled={isChargeDisabled}
                />

                {/* Service Section */}
                <FeatureGuard key="5" feature="hasServicesOnOrderDetails">
                  {!!orderedServices?.length && orderedServices?.length > 0 && (
                    <ServiceList
                      refetch={refetch}
                      services={orderedServices || []}
                      onEditService={() => onEditProduct('service')}
                    />
                  )}
                </FeatureGuard>

                {/* Products Section */}
                {!!orderedProducts?.length && orderedProducts?.length > 0 && (
                  <ProductList
                    key="6"
                    refetch={refetch}
                    products={orderedProducts || []}
                    onEditProduct={() => onEditProduct('product')}
                  />
                )}
              </OrganizerBySequence>
            </div>
          </div>
        </div>
      </Modal>
      <ActivityNotesModal
        orderId={
          isMergedOrder
            ? activeTab?.sequential_order_number?.toString()
            : orderNumber
        }
        isOpen={isActivityNotesModalOpen}
        onClose={() => setIsActivityNotesModalOpen(false)}
      />
      {showAddressListModal && (
        <AddressListModal
          isOpen={showAddressListModal}
          onClose={() => setShowAddressListModal(false)}
          addresses={data?.customer.delivery_addresses || []}
          deliveryAddressId={Number(deliveryAddressId) || 0}
          setDeliveryAddressId={(id: number) => setDeliveryAddressId(id)}
          openNewAddressModal={() => setShowNewAddressModal(true)}
          addressIsUpdating={addressIsUpdating}
          addressChangeHandler={() =>
            addressChangeHandler(deliveryAddressId || 0)
          }
        />
      )}
      {showNewAddressModal && (
        <AddAddressModal
          isOpen={showNewAddressModal}
          onClose={() => setShowNewAddressModal(false)}
          onSuccess={(data) => {
            refetchOrders();
            refetch();
            setDeliveryAddressId(data.id);
          }}
          createNewAddress={createNewAddress}
          customer_id={data?.customer.id as unknown as number}
        />
      )}

      {showEditModal && (
        <EditModal
          requestId={data?.id as number}
          refetch={refetch}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditType('none');
          }}
          product={editType === 'service' ? orderedServices : orderedProducts}
          editType={editType}
        />
      )}
      {showPaymentModal && (
        <PayModal
          data={data!}
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          paymentUrl={paymentUrl}
          handleSendNotification={handleNotifyCustomer}
        />
      )}
      {showCardModal && (
        <CardModal
          cards={cards}
          selectedCard={selectedCard}
          setCardIs={setCardIs}
          isOpen={showCardModal}
          onClose={() => setShowCardModal(false)}
          cardId={cardId}
          chargeCard={chargeCard}
          customerId={data?.customer.id as unknown as string}
          prescriptions={data?.prescriptions as any[]}
          copayItems={copayItems || []}
          orderItems={orderItems || []}
          scheme={ORDER_ITEMS_SCHEMA}
        />
      )}
      {showNoteForWorkerModal && (
        <NoteForWorkerModal
          isOpen={showNoteForWorkerModal}
          onClose={() => setShowNoteForWorkerModal(false)}
          order={order}
          refetch={() => {
            refetchOrders();
            refetch();
          }}
        />
      )}
      {showChargeModal && (
        <ManualChargeModal
          isOpen={showChargeModal}
          onClose={() => setShowChargeModal(false)}
          schema={ORDER_ITEMS_SCHEMA}
          data={orderItems || []}
          copayItems={copayItems || []}
          onChargeManually={() =>
            onChargeManually(() => setShowChargeModal(false))
          }
          isCharging={isCharging}
          disabled={copayItems.length === 0 || isCharging}
          form={form}
          manualChargeType={manualChargeType}
        />
      )}
      {showMessageModal && (
        <MessageModal
          selectedTemplate={selectedTemplate}
          messageTemplates={messageTemplates!}
          isOpen={showMessageModal}
          sendChannels={sendChannels}
          sendMessage={sendMessage}
          onSelect={(option) => {
            setSelectedTemplate(option!);
            setMessage(option?.value as string);
          }}
          username={customerName}
          onClose={() => {
            setShowMessageModal(false);
            setMessage('');
            setSelectedTemplate(undefined);
          }}
          handleChannelChange={(name: string, isChecked: boolean) =>
            setSendChannels((prev) => ({...prev, [name]: isChecked}))
          }
        >
          <textarea
            className={styles.messageTextarea}
            placeholder="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </MessageModal>
      )}
      {showDeleteModal && (
        <DeleteModal
          openModal={showDeleteModal}
          setOpenModal={setShowDeleteModal}
          onDelete={deleteOrder(() => setShowDeleteModal(false))}
          text="Are you sure you want to delete this order?"
        />
      )}
    </Fragment>
  );
};

export default OrderDetailsModal;
